/**
 * Custom Hook for Group Chat with PubNub
 *
 * Provides all group-chat functionality:
 * - Subscribing to group channels
 * - Sending / editing / deleting messages
 * - Fetching history with pagination
 * - Presence (online members list & count)
 * - Typing indicators (shows WHO is typing – WhatsApp style)
 * - Reactions, receipts, read-more, reply, etc.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import PubNub from "pubnub";
import { usePubNub } from "pubnub-react";
import { generateGroupChannel, CHANNEL_PREFIXES } from "./config";
import {
  ChatMessage,
  ChatFileData,
  ChatParticipant,
  GroupTypingUser,
  MessageReaction,
  MessageReactionsMap,
  MessageReceipt,
  MessageReceiptsMap,
  ReplyTo,
} from "./types";
import { sendMessageService, pubnubTokenService } from "src/services";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface UseGroupChatOptions {
  currentUserId: string;
  currentUserName: string;
  groupId: string;
  members: ChatParticipant[]; // all group members (for name resolution)
  channelId?: string; // optional — use the actual channel from API instead of generating from groupId
  autoSubscribe?: boolean;
  historyCount?: number;
  isRemoved?: boolean; // true when current user was removed — skip subscribe, only fetch history
}

export interface UseGroupChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  isConnected: boolean;

  // Presence
  onlineMembers: string[];      // array of online user IDs
  onlineCount: number;

  // Typing — array of currently-typing users (WhatsApp style)
  typingUsers: GroupTypingUser[];

  // Reactions / receipts / deletedForMe
  reactions: MessageReactionsMap;
  receipts: MessageReceiptsMap;
  deletedForMe: Set<string>;

  // Actions
  sendMessage: (text: string, files?: File[], replyTo?: ReplyTo) => Promise<void>;
  editMessage: (messageId: string, newText: string) => Promise<void>;
  deleteMessage: (messageId: string, deleteForEveryone: boolean) => Promise<void>;
  sendTypingIndicator: (isTyping: boolean) => void;
  addReaction: (messageTimetoken: string, emoji: string) => Promise<void>;
  removeReaction: (messageTimetoken: string, actionTimetoken: string) => Promise<void>;
  markAsRead: (messageTimetoken: string) => void;
  getMessageStatus: (msg: ChatMessage) => "sending" | "sent" | "delivered" | "read" | "failed";
  loadMoreMessages: () => Promise<void>;
  loadMessagesToTimetoken: (targetTimetoken: string) => Promise<boolean>;
  clearMessages: () => void;
  channelId: string;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export const useGroupChat = ({
  currentUserId,
  currentUserName,
  groupId,
  members,
  channelId: channelIdProp,
  autoSubscribe = true,
  historyCount = 100,
  isRemoved = false,
}: UseGroupChatOptions): UseGroupChatReturn => {
  const pubnub = usePubNub();

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(true);

  // Presence
  const [onlineMembers, setOnlineMembers] = useState<string[]>([]);

  // Typing – map of userId -> timeout, derived into typingUsers[]
  const [typingUsersMap, setTypingUsersMap] = useState<Record<string, NodeJS.Timeout>>({});

  // Reactions / receipts
  const [reactions, setReactions] = useState<MessageReactionsMap>({});
  const [receipts, setReceipts] = useState<MessageReceiptsMap>({});
  const [deletedForMe, setDeletedForMe] = useState<Set<string>>(new Set());

  const sentReceiptsRef = useRef<Set<string>>(new Set());
  const oldestTimetokenRef = useRef<string | null>(null);
  // Track timetokens loaded from history so we skip delivery receipts for them
  const historyTimetokensRef = useRef<Set<string>>(new Set());

  // Ref to track latest isRemoved value without triggering effect re-runs
  const isRemovedRef = useRef(isRemoved);
  // Generation counter to discard stale fetchHistory results during rapid state changes
  const fetchGenRef = useRef(0);

  // Use the provided channelId (from API) if available; otherwise generate from groupId
  const channelId = channelIdProp || generateGroupChannel(groupId);
  const typingChannel = `${channelId}-typing`;

  // Resolve member name from id
  const resolveName = useCallback(
    (userId: string) => {
      const m = members.find((p) => p.id === userId);
      return m?.name ?? userId;
    },
    [members],
  );

  // Derive typingUsers array from the map (exclude self)
  const typingUsers: GroupTypingUser[] = Object.keys(typingUsersMap)
    .filter((uid) => uid !== currentUserId)
    .map((uid) => ({ userId: uid, userName: resolveName(uid) }));

  const onlineCount = onlineMembers.length;

  /* ---------- helpers ---------- */

  const generateMessageId = () =>
    `${currentUserId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  /* ---------- listeners ---------- */

  const handleMessage = useCallback(
    (event: PubNub.MessageEvent) => {
      if (event.channel !== channelId) return;
      const messageData = event.message as ChatMessage;

      // Skip raw PubNub file events (auto-published by sendFile)
      // They lack our custom id/senderId — the real message is published separately
      if (!messageData.id || !messageData.senderId) return;

      setMessages((prev) => {
        if (prev.some((m) => m.id === messageData.id)) {
          return prev.map((m) =>
            m.id === messageData.id && !m.timetoken
              ? { ...m, timetoken: event.timetoken, status: "delivered" as const }
              : m,
          );
        }
        return [...prev, { ...messageData, status: "delivered" as const, timetoken: event.timetoken }];
      });
    },
    [channelId],
  );

  // Keep a ref of member IDs so presence callbacks always see latest membership
  const memberIdsRef = useRef<Set<string>>(new Set(members.map((m) => m.id)));
  useEffect(() => {
    memberIdsRef.current = new Set(members.map((m) => m.id));
    // When members change, prune onlineMembers to only include current members
    setOnlineMembers((prev) => prev.filter((id) => memberIdsRef.current.has(id)));
  }, [members]);

  const handlePresence = useCallback(
    (event: PubNub.PresenceEvent) => {
      // Only track presence from global channel (login-based status)
      if (event.channel !== CHANNEL_PREFIXES.GLOBAL_PRESENCE) return;

      // Only track users who are members of this group
      if (!memberIdsRef.current.has(event.uuid)) return;

      if (event.action === "join") {
        setOnlineMembers((prev) =>
          prev.includes(event.uuid) ? prev : [...prev, event.uuid],
        );
      } else if (event.action === "leave" || event.action === "timeout") {
        setOnlineMembers((prev) => prev.filter((id) => id !== event.uuid));
      }
    },
    [],
  );

  const handleSignal = useCallback(
    (event: PubNub.SignalEvent) => {
      if (event.channel !== typingChannel) return;
      if (event.publisher === currentUserId) return;

      const signal = event.message as { typing: boolean; userName?: string };

      if (signal.typing) {
        setTypingUsersMap((prev) => {
          // Clear existing timeout for this user if any
          if (prev[event.publisher]) clearTimeout(prev[event.publisher]);
          const timeout = setTimeout(() => {
            setTypingUsersMap((p) => {
              const copy = { ...p };
              delete copy[event.publisher];
              return copy;
            });
          }, 5000);
          return { ...prev, [event.publisher]: timeout };
        });
      } else {
        setTypingUsersMap((prev) => {
          if (prev[event.publisher]) clearTimeout(prev[event.publisher]);
          const copy = { ...prev };
          delete copy[event.publisher];
          return copy;
        });
      }
    },
    [typingChannel, currentUserId],
  );

  const handleStatus = useCallback((event: PubNub.StatusEvent) => {
    if (event.category === "PNConnectedCategory") {
      setIsConnected(true);
      setError(null);
    } else if (event.category === "PNNetworkDownCategory") {
      setIsConnected(false);
      setError("Connection lost");
    } else if (event.category === "PNNetworkUpCategory") {
      setIsConnected(true);
      setError(null);
    } else if (event.category === "PNAccessDeniedCategory") {
      // Only log — don't block the chat. Token may lack some channel grants.
      const deniedChannels = (event as any)?.errorData?.payload?.channels || [];
      console.warn("[PubNub-Group] Access denied for:", deniedChannels);
    }
  }, []);

  const handleMessageAction = useCallback(
    (event: any) => {
      if (event.channel !== channelId) return;
      const { messageTimetoken, type, value, uuid, actionTimetoken } = event.data;

      if (type === "reaction") {
        if (event.event === "added") {
          setReactions((prev) => {
            const existing = prev[messageTimetoken] || [];
            if (existing.some((r) => r.actionTimetoken === actionTimetoken)) return prev;
            return { ...prev, [messageTimetoken]: [...existing, { emoji: value, userId: uuid, actionTimetoken }] };
          });
        } else if (event.event === "removed") {
          setReactions((prev) => ({
            ...prev,
            [messageTimetoken]: (prev[messageTimetoken] || []).filter(
              (r) => r.actionTimetoken !== actionTimetoken,
            ),
          }));
        }
      }

      if (type === "receipt") {
        if (event.event === "added") {
          setReceipts((prev) => {
            const existing = prev[messageTimetoken] || {};
            const list = (existing[value as keyof typeof existing] || []) as MessageReceipt[];
            if (list.some((r) => r.actionTimetoken === actionTimetoken)) return prev;
            return {
              ...prev,
              [messageTimetoken]: { ...existing, [value]: [...list, { type: value, userId: uuid, actionTimetoken }] },
            };
          });
        } else if (event.event === "removed") {
          setReceipts((prev) => {
            const existing = prev[messageTimetoken] || {};
            const list = (existing[value as keyof typeof existing] || []) as MessageReceipt[];
            return {
              ...prev,
              [messageTimetoken]: { ...existing, [value]: list.filter((r) => r.actionTimetoken !== actionTimetoken) },
            };
          });
        }
      }

      if (type === "edited" && event.event === "added") {
        try {
          const editData = JSON.parse(value);
          setMessages((prev) =>
            prev.map((m) =>
              String(m.timetoken) === messageTimetoken
                ? { ...m, text: editData.text, isEdited: true, editedAt: editData.editedAt }
                : m,
            ),
          );
        } catch {}
      }

      if (type === "deleted" && event.event === "added") {
        setMessages((prev) =>
          prev.map((m) =>
            String(m.timetoken) === messageTimetoken
              ? { ...m, isDeleted: true, deletedBy: uuid, text: "" }
              : m,
          ),
        );
      }

      if (type === "deletedForMe" && event.event === "added" && uuid === currentUserId) {
        setDeletedForMe((prev) => new Set([...prev, value]));
      }
    },
    [channelId, currentUserId],
  );

  /* ---------- process history ---------- */

  const processHistoryMessages = useCallback(
    (historyMessages: any[]) => {
      const formattedMessages: ChatMessage[] = [];
      const reactionsMap: MessageReactionsMap = {};
      const receiptsMap: MessageReceiptsMap = {};
      const deletedForMeSet: Set<string> = new Set();

      historyMessages.forEach((item: any) => {
        // Skip raw PubNub file events that lack our custom fields
        if (!item.message?.id || !item.message?.senderId) return;

        // Derive timestamp from timetoken when message has no timestamp
        const derivedTimestamp =
          item.message.timestamp ||
          Math.floor(Number(BigInt(item.timetoken) / BigInt(10000)));

        const msg: ChatMessage = {
          ...(item.message as ChatMessage),
          timestamp: derivedTimestamp,
          status: "delivered" as const,
          timetoken: item.timetoken,
        };

        // edits
        if (item.actions?.edited) {
          const keys = Object.keys(item.actions.edited);
          if (keys.length) {
            try {
              const d = JSON.parse(keys[keys.length - 1]);
              msg.text = d.text;
              msg.isEdited = true;
              msg.editedAt = d.editedAt;
            } catch {}
          }
        }

        // deletes
        if (item.actions?.deleted) {
          const dKeys = Object.keys(item.actions.deleted);
          if (dKeys.length) {
            const actions = item.actions.deleted[dKeys[0]];
            if (actions?.length) {
              msg.isDeleted = true;
              msg.deletedBy = actions[0].uuid;
              msg.text = "";
            }
          }
        }

        // deletedForMe
        if (item.actions?.deletedForMe) {
          Object.entries(item.actions.deletedForMe).forEach(([messageIdValue, actions]: [string, any]) => {
            if (actions.find((a: any) => a.uuid === currentUserId)) {
              deletedForMeSet.add(messageIdValue);
            }
          });
        }

        formattedMessages.push(msg);

        // Mark as historical so delivery receipt useEffect skips it
        historyTimetokensRef.current.add(String(item.timetoken));

        // Pre-seed sentReceiptsRef for ALL receipt types for this message
        // so the auto-deliver effect never re-sends for historical messages
        sentReceiptsRef.current.add(`delivered-${item.timetoken}`);

        // reactions
        if (item.actions?.reaction) {
          const mr: MessageReaction[] = [];
          Object.entries(item.actions.reaction).forEach(([emoji, actions]: [string, any]) => {
            actions.forEach((a: any) => mr.push({ emoji, userId: a.uuid, actionTimetoken: a.actionTimetoken }));
          });
          if (mr.length) reactionsMap[item.timetoken] = mr;
        }

        // receipts
        if (item.actions?.receipt) {
          const ro: any = {};
          Object.entries(item.actions.receipt).forEach(([rType, actions]: [string, any]) => {
            ro[rType] = actions.map((a: any) => {
              if (a.uuid === currentUserId) sentReceiptsRef.current.add(`${rType}-${item.timetoken}`);
              return { type: rType, userId: a.uuid, actionTimetoken: a.actionTimetoken };
            });
          });
          if (Object.keys(ro).length) receiptsMap[item.timetoken] = ro;
        }
      });

      return { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet };
    },
    [currentUserId],
  );

  /* ---------- fetch history ---------- */

  const fetchHistory = useCallback(
    async (options: { count: number; retried?: boolean }) => {
      const thisGen = ++fetchGenRef.current;
      setIsLoading(true);
      setHasMore(true);
      oldestTimetokenRef.current = null;

      try {
        const response = await pubnub.fetchMessages({
          channels: [channelId],
          count: options.count,
          includeMeta: true,
          includeMessageActions: true,
        });

        // Discard result if a newer fetch was started (prevents race conditions
        // during rapid isRemoved toggling or group switching)
        if (fetchGenRef.current !== thisGen) return;

        const hist = response.channels[channelId] || [];
        if (hist.length < options.count) setHasMore(false);
        if (hist.length > 0) oldestTimetokenRef.current = String(hist[0].timetoken);

        const { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet } =
          processHistoryMessages(hist);

        setMessages(formattedMessages);
        setReactions(reactionsMap);
        setReceipts(receiptsMap);
        setDeletedForMe(deletedForMeSet);
        setError(null);
      } catch (err: any) {
        // Discard error handling if a newer fetch was started
        if (fetchGenRef.current !== thisGen) return;

        console.warn("[PubNub-Group] Failed to fetch history:", err);

        // For removed users, 403 is expected when PAM token doesn't include this channel.
        // Try refreshing the token with the specific channel and retry once.
        const is403 = err?.status?.statusCode === 403 || err?.message?.includes?.('Forbidden');
        if (isRemovedRef.current && is403 && !options.retried) {
          try {
            const token = await pubnubTokenService(channelId);
            if (token) {
              pubnub.setToken(token);
              // Retry once with fresh token
              if (fetchGenRef.current === thisGen) {
                setIsLoading(false);
              }
              return fetchHistory({ count: options.count, retried: true });
            }
          } catch (tokenErr) {
            console.warn("[PubNub-Group] Token refresh for removed channel failed:", tokenErr);
          }
        }

        // Only clear messages on failure if this is NOT a transition fetch
        // (i.e., don't clear existing history when a background refetch fails)
        if (!options.retried) {
          setMessages((prev) => prev.length === 0 ? prev : []);
        }
        setHasMore(false);
      } finally {
        if (fetchGenRef.current === thisGen) {
          setIsLoading(false);
        }
      }
    },
    [pubnub, channelId, processHistoryMessages],
  );

  /* ---------- load more (pagination) ---------- */

  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMore || !hasMore || !oldestTimetokenRef.current) return;
    setIsLoadingMore(true);
    const pageSize = 25;

    try {
      const response = await pubnub.fetchMessages({
        channels: [channelId],
        count: pageSize,
        start: oldestTimetokenRef.current,
        includeMeta: true,
        includeMessageActions: true,
      });

      const hist = response.channels[channelId] || [];
      if (hist.length === 0) { setHasMore(false); setIsLoadingMore(false); return; }
      if (hist.length < pageSize) setHasMore(false);
      oldestTimetokenRef.current = String(hist[0].timetoken);

      const { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet } =
        processHistoryMessages(hist);

      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.timetoken));
        return [...formattedMessages.filter((m) => !existing.has(m.timetoken)), ...prev];
      });
      setReactions((prev) => ({ ...prev, ...reactionsMap }));
      setReceipts((prev) => ({ ...prev, ...receiptsMap }));
      setDeletedForMe((prev) => new Set([...prev, ...deletedForMeSet]));
    } catch {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pubnub, channelId, isLoadingMore, hasMore, processHistoryMessages]);

  /* ---------- load messages until a target timetoken (for reply-scroll) ---------- */

  const loadMessagesToTimetoken = useCallback(
    async (targetTimetoken: string): Promise<boolean> => {
      // Already loaded?
      const alreadyLoaded = messages.some(
        (m) => String(m.timetoken) === String(targetTimetoken),
      );
      if (alreadyLoaded) return true;

      // Fetch from the target timetoken up to our currently loaded oldest message.
      // PubNub fetchMessages:
      //   end   = lower boundary (inclusive) — messages >= this timetoken
      //   start = upper boundary (exclusive) — messages <  this timetoken
      // With includeMessageActions the cap is 25 per request, so we loop.
      const PAGE = 25;
      let cursor: string | undefined = oldestTimetokenRef.current || undefined;
      let found = false;
      let attempts = 0;
      const MAX_ATTEMPTS = 40; // 40 × 25 = 1 000 msgs max
      // end is inclusive — subtract 1 so the target itself is included
      const endToken = (BigInt(targetTimetoken) - BigInt(1)).toString();

      while (!found && attempts < MAX_ATTEMPTS) {
        attempts++;
        try {
          const fetchParams: any = {
            channels: [channelId],
            count: PAGE,
            end: endToken, // inclusive lower bound — fetch msgs >= target
            includeMeta: true,
            includeMessageActions: true,
          };
          // Only set start (upper bound) when we have a cursor so we don't
          // re-fetch messages we already have.
          if (cursor) fetchParams.start = cursor;

          const response = await pubnub.fetchMessages(fetchParams);
          const hist = response.channels[channelId] || [];

          if (hist.length === 0) {
            break;
          }

          // Update cursor & oldest ref to the oldest message we just fetched
          const oldestFetched = String(hist[0].timetoken);
          cursor = oldestFetched;
          if (
            !oldestTimetokenRef.current ||
            BigInt(oldestFetched) < BigInt(oldestTimetokenRef.current)
          ) {
            oldestTimetokenRef.current = oldestFetched;
          }

          const { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet } =
            processHistoryMessages(hist);

          // Prepend to existing messages (avoiding duplicates)
          setMessages((prev) => {
            const existing = new Set(prev.map((m) => String(m.timetoken)));
            const newMsgs = formattedMessages.filter(
              (m) => !existing.has(String(m.timetoken)),
            );
            return [...newMsgs, ...prev];
          });
          setReactions((prev) => ({ ...prev, ...reactionsMap }));
          setReceipts((prev) => ({ ...prev, ...receiptsMap }));
          setDeletedForMe((prev) => new Set([...prev, ...deletedForMeSet]));

          // Check if the target message was in this batch
          if (
            hist.some(
              (h: any) => String(h.timetoken) === String(targetTimetoken),
            )
          ) {
            found = true;
          }

          // If the response provides a "more" object (PubNub truncation with
          // message-actions), use its start as the next cursor.
          if ((response as any).more?.start) {
            cursor = (response as any).more.start;
          }
        } catch (err) {
          console.error("[PubNub] loadMessagesToTimetoken error:", err);
          break;
        }
      }
      return found;
    },
    [pubnub, channelId, messages, processHistoryMessages],
  );

  /* ---------- subscribe ---------- */

  // ── Main subscribe effect — triggers on channel/user identity changes, NOT on isRemoved ──
  // This prevents full message clearing when a user is rapidly added/removed.
  useEffect(() => {
    if (!autoSubscribe || !currentUserId || !groupId) return;

    // Reset all state for a clean slate when switching groups
    setMessages([]);
    setReactions({});
    setReceipts({});
    setDeletedForMe(new Set());
    setIsLoading(false);
    setIsLoadingMore(false);
    setHasMore(true);
    setError(null);
    oldestTimetokenRef.current = null;
    sentReceiptsRef.current.clear();
    historyTimetokensRef.current.clear();

    const listener = {
      message: handleMessage,
      presence: handlePresence,
      signal: handleSignal,
      status: handleStatus,
      messageAction: handleMessageAction,
    };

    pubnub.addListener(listener);

    // For removed users, skip subscribe to avoid 403 from PAM.
    // Only fetch history (read-only mode).
    if (!isRemovedRef.current) {
      pubnub.subscribe({ channels: [channelId, typingChannel], withPresence: true });
    }

    // fetch initial history (even for removed users)
    fetchHistory({ count: 25 });

    let t1: ReturnType<typeof setTimeout> | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;

    // Only check presence for active (non-removed) users
    if (!isRemovedRef.current) {
      const checkPresence = () => {
        if (isRemovedRef.current) return; // skip if removed since effect started
        pubnub
          .hereNow({ channels: [CHANNEL_PREFIXES.GLOBAL_PRESENCE], includeUUIDs: true })
          .then((res) => {
            const occ = res.channels[CHANNEL_PREFIXES.GLOBAL_PRESENCE]?.occupants || [];
            const allOnlineIds = occ.map((o) => o.uuid);
            // Filter to only group members
            setOnlineMembers(allOnlineIds.filter((id) => memberIdsRef.current.has(id)));
          })
          .catch(() => {});
      };

      t1 = setTimeout(checkPresence, 2000);
      interval = setInterval(checkPresence, 15000);
    }

    return () => {
      pubnub.removeListener(listener);
      // Always clean up typing channel on unmount/group-switch
      pubnub.unsubscribe({ channels: [typingChannel] });
      if (t1) clearTimeout(t1);
      if (interval) clearInterval(interval);
      // clear typing timeouts
      Object.values(typingUsersMap).forEach((t) => clearTimeout(t));
    };
  }, [pubnub, channelId, typingChannel, autoSubscribe, currentUserId, groupId]);

  // ── Separate effect for isRemoved transitions ──
  // Handles subscribe/unsubscribe without clearing messages (preserves history for removed users)
  const prevIsRemovedRef = useRef(isRemoved);
  useEffect(() => {
    const prevVal = prevIsRemovedRef.current;
    prevIsRemovedRef.current = isRemoved;
    isRemovedRef.current = isRemoved;

    // Skip on initial mount — the main subscribe effect handles it
    if (prevVal === isRemoved) return;

    if (isRemoved) {
      // User was just removed — unsubscribe from typing channel
      // Keep existing messages visible (history up to removal time)
      pubnub.unsubscribe({ channels: [typingChannel] });
    } else {
      // User was re-added — re-subscribe and refresh history
      pubnub.subscribe({ channels: [channelId, typingChannel], withPresence: true });
      fetchHistory({ count: 25 });
    }
  }, [isRemoved, pubnub, channelId, typingChannel, fetchHistory]);

  /* ---------- send message (supports multiple files + reply) ---------- */

  const sendMessage = useCallback(
    async (text: string, files?: File[], replyTo?: ReplyTo) => {
      // Prevent removed users from sending messages
      if (isRemovedRef.current) return;
      if (!text.trim() && (!files || files.length === 0)) return;

      const id = generateMessageId();
      const timestamp = Date.now();

      // ---------- Upload files to PubNub (sequentially) ----------
      const uploadedFiles: ChatFileData[] = [];

      if (files && files.length > 0) {
        for (const file of files) {
          try {
            const sendFileResponse = await pubnub.sendFile({
              channel: channelId,
              file: file,
              message: {
                text: files.length === 1 ? text.trim() : "",
                type: "file",
              },
              storeInHistory: true,
            });

            const fileId = (sendFileResponse as any).id;
            const fileName = (sendFileResponse as any).name;
            const fileTimetoken = (sendFileResponse as any).timetoken;

            if (!fileId || !fileName) {
              throw new Error("Invalid file upload response");
            }

            uploadedFiles.push({
              id: fileId,
              name: fileName,
              timetoken: fileTimetoken,
              type: file.type,
              size: file.size,
            });
          } catch (err: any) {
            console.error(`[PubNub-Group] File upload failed for ${file.name}:`, err);
            setError(`Failed to upload ${file.name}`);
            throw err;
          }
        }
      }

      // ---------- Build message object ----------
      const hasFiles = uploadedFiles.length > 0;

      const message: ChatMessage = {
        id,
        text: text.trim(),
        senderId: currentUserId,
        senderName: currentUserName,
        timestamp,
        channelId,
        type: hasFiles ? "file" : "text",
        status: "sending",
        ...(replyTo && { replyTo }),
        ...(hasFiles && { file: uploadedFiles[0] }),
        ...(uploadedFiles.length > 1 && { files: uploadedFiles }),
      };

      // Optimistic update
      setMessages((prev) => [...prev, message]);
      sendTypingIndicator(false);

      try {
        const res = await pubnub.publish({ channel: channelId, message, storeInHistory: true });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === id ? { ...m, status: "sent" as const, timetoken: res.timetoken } : m
          )
        );

        // Backend sync (fire-and-forget)
        sendMessageService(message, files).catch(console.warn);
      } catch (err: any) {
        if (err?.status?.statusCode === 403) {
          setError("Access denied.");
        } else {
          setError("Failed to send message");
        }
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, status: "failed" } : m))
        );
      }
    },
    [pubnub, channelId, currentUserId, currentUserName],
  );

  /* ---------- typing indicator ---------- */

  const sendTypingIndicator = useCallback(
    (isTyping: boolean) => {
      if (isRemovedRef.current) return;
      pubnub
        .signal({ channel: typingChannel, message: { typing: isTyping, userName: currentUserName } })
        .catch(() => {});
    },
    [pubnub, typingChannel, currentUserName],
  );

  /* ---------- edit / delete ---------- */

  const editMessage = useCallback(
    async (messageId: string, newText: string) => {
      if (!newText.trim()) return;
      const target = messages.find((m) => m.id === messageId && m.senderId === currentUserId);
      if (!target?.timetoken) return;

      const editedAt = Date.now();
      const tt = String(target.timetoken);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, text: newText.trim(), isEdited: true, editedAt } : m)));

      try {
        await pubnub.addMessageAction({
          channel: channelId,
          messageTimetoken: tt,
          action: { type: "edited", value: JSON.stringify({ text: newText.trim(), editedAt }) },
        });
      } catch {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, text: target.text, isEdited: target.isEdited, editedAt: target.editedAt } : m)));
      }
    },
    [pubnub, channelId, currentUserId, messages],
  );

  const deleteMessage = useCallback(
    async (messageId: string, deleteForEveryone: boolean) => {
      if (deleteForEveryone) {
        const target = messages.find((m) => m.id === messageId && m.senderId === currentUserId);
        if (!target?.timetoken) return;
        const tt = String(target.timetoken);
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, isDeleted: true, deletedBy: currentUserId, text: "" } : m)));

        try {
          await pubnub.addMessageAction({ channel: channelId, messageTimetoken: tt, action: { type: "deleted", value: "true" } });
        } catch {
          setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, isDeleted: false, deletedBy: undefined, text: target.text } : m)));
        }
      } else {
        const target = messages.find((m) => m.id === messageId);
        if (!target?.timetoken) return;
        const tt = String(target.timetoken);
        setDeletedForMe((prev) => new Set([...prev, messageId]));

        try {
          await pubnub.addMessageAction({ channel: channelId, messageTimetoken: tt, action: { type: "deletedForMe", value: messageId } });
        } catch {
          setDeletedForMe((prev) => { const s = new Set(prev); s.delete(messageId); return s; });
        }
      }
    },
    [pubnub, channelId, currentUserId, messages],
  );

  /* ---------- reactions (one reaction per user per message) ---------- */

  const addReaction = useCallback(
    async (messageTimetoken: string, emoji: string) => {
      const existing = (reactions[messageTimetoken] || []).find(
        (r) => r.userId === currentUserId,
      );

      // Same emoji → toggle off (remove it)
      if (existing?.emoji === emoji) {
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: (prev[messageTimetoken] || []).filter(
            (r) => r.actionTimetoken !== existing.actionTimetoken,
          ),
        }));
        try {
          await pubnub.removeMessageAction({
            channel: channelId,
            messageTimetoken,
            actionTimetoken: existing.actionTimetoken,
          });
        } catch {
          // Restore on failure
          setReactions((prev) => ({
            ...prev,
            [messageTimetoken]: [...(prev[messageTimetoken] || []), existing],
          }));
        }
        return;
      }

      // Different emoji → remove old reaction first, then add new one
      if (existing) {
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: (prev[messageTimetoken] || []).filter(
            (r) => r.actionTimetoken !== existing.actionTimetoken,
          ),
        }));
        try {
          await pubnub.removeMessageAction({
            channel: channelId,
            messageTimetoken,
            actionTimetoken: existing.actionTimetoken,
          });
        } catch {
          // Restore old on failure and bail
          setReactions((prev) => ({
            ...prev,
            [messageTimetoken]: [...(prev[messageTimetoken] || []), existing],
          }));
          return;
        }
      }

      // Add the new reaction
      const tempAT = `temp-${Date.now()}`;
      setReactions((prev) => ({
        ...prev,
        [messageTimetoken]: [
          ...(prev[messageTimetoken] || []),
          { emoji, userId: currentUserId, actionTimetoken: tempAT },
        ],
      }));

      try {
        const res = await pubnub.addMessageAction({
          channel: channelId,
          messageTimetoken,
          action: { type: "reaction", value: emoji },
        });
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: (prev[messageTimetoken] || []).map((r) =>
            r.actionTimetoken === tempAT
              ? { ...r, actionTimetoken: res.data.actionTimetoken }
              : r,
          ),
        }));
      } catch {
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: (prev[messageTimetoken] || []).filter(
            (r) => r.actionTimetoken !== tempAT,
          ),
        }));
      }
    },
    [pubnub, channelId, currentUserId, reactions],
  );

  const removeReaction = useCallback(
    async (messageTimetoken: string, actionTimetoken: string) => {
      const removed = reactions[messageTimetoken]?.find(
        (r) => r.actionTimetoken === actionTimetoken,
      );
      setReactions((prev) => ({
        ...prev,
        [messageTimetoken]: (prev[messageTimetoken] || []).filter(
          (r) => r.actionTimetoken !== actionTimetoken,
        ),
      }));

      try {
        await pubnub.removeMessageAction({
          channel: channelId,
          messageTimetoken,
          actionTimetoken,
        });
      } catch {
        if (removed) {
          setReactions((prev) => ({
            ...prev,
            [messageTimetoken]: [...(prev[messageTimetoken] || []), removed],
          }));
        }
      }
    },
    [pubnub, channelId, reactions],
  );

  /* ---------- receipts ---------- */

  const markAsRead = useCallback(
    (messageTimetoken: string) => {
      if (isRemovedRef.current) return;
      const key = `read-${messageTimetoken}`;
      if (sentReceiptsRef.current.has(key)) return;
      sentReceiptsRef.current.add(key);
      pubnub
        .addMessageAction({ channel: channelId, messageTimetoken, action: { type: "receipt", value: "read" } })
        .catch((err: any) => {
          if (err?.status?.statusCode !== 409) sentReceiptsRef.current.delete(key);
        });
    },
    [pubnub, channelId],
  );

  // Auto deliver receipts ONLY for new real-time messages (not from history).
  // Historical messages are pre-seeded in sentReceiptsRef by processHistoryMessages.
  useEffect(() => {
    if (isRemoved) return;
    const pending: { tt: string }[] = [];
    for (const msg of messages) {
      if (!msg.timetoken || msg.senderId === currentUserId) continue;
      const tt = String(msg.timetoken);
      const key = `delivered-${tt}`;
      if (sentReceiptsRef.current.has(key)) continue;
      sentReceiptsRef.current.add(key);
      pending.push({ tt });
    }
    // Batch: fire all at once without waiting
    pending.forEach(({ tt }) => {
      pubnub
        .addMessageAction({
          channel: channelId,
          messageTimetoken: tt,
          action: { type: "receipt", value: "delivered" },
        })
        .catch((err: any) => {
          if (err?.status?.statusCode !== 409) sentReceiptsRef.current.delete(`delivered-${tt}`);
        });
    });
  }, [messages, currentUserId, pubnub, channelId, isRemoved]);

  /* ---------- message status ---------- */

  // Number of OTHER members who must read/deliver for full ticks
  const otherMemberCount = Math.max(members.length - 1, 1);

  const getMessageStatus = useCallback(
    (msg: ChatMessage): "sending" | "sent" | "delivered" | "read" | "failed" => {
      if (msg.senderId !== currentUserId) return "delivered";
      if (msg.status === "sending") return "sending";
      if (msg.status === "failed") return "failed";
      if (!msg.timetoken) return "sent";

      const tt = String(msg.timetoken);
      const r = receipts[tt];

      // Blue tick only when ALL other members have read
      if (r?.read) {
        const uniqueReaders = new Set(r.read.map((rc) => rc.userId));
        if (uniqueReaders.size >= otherMemberCount) return "read";
      }
      // Double grey tick only when ALL other members received
      if (r?.delivered) {
        const uniqueDelivered = new Set(r.delivered.map((rc) => rc.userId));
        if (uniqueDelivered.size >= otherMemberCount) return "delivered";
      }
      return "sent";
    },
    [currentUserId, receipts, otherMemberCount],
  );

  /* ---------- clear ---------- */

  const clearMessages = useCallback(() => {
    setMessages([]);
    setReactions({});
    setReceipts({});
  }, []);

  /* ---------- return ---------- */

  return {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    isConnected,
    onlineMembers,
    onlineCount,
    typingUsers,
    reactions,
    receipts,
    deletedForMe,
    sendMessage,
    editMessage,
    deleteMessage,
    sendTypingIndicator,
    addReaction,
    removeReaction,
    markAsRead,
    getMessageStatus,
    loadMoreMessages,
    loadMessagesToTimetoken,
    clearMessages,
    channelId,
  };
};

export default useGroupChat;
